import { Billboard, Html } from '@react-three/drei'
import { Camera, Size } from '@react-three/fiber'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Group, Object3D, Vector3 } from 'three'
import { useSceneStore } from '../../../a.scenes/GameScene/store'
import { SEL_SELECTED_STATE_UUID, SEL_STATE_BY_UUID, SEL_TEAM_BY_UUID } from '../../../c.managers/CharactersManager/selectors'
import { CharacterBaseState } from '../../../c.managers/CharactersManager/types'
import { SEL_BATTLE_STATE } from '../../../c.managers/LevelManager/selectors'
import { BATTLE_STATES, COMMANDERS } from '../../../c.managers/LevelManager/store.d'
import { CharacterAction } from '../../../f.assets/Characters/scripts/classes/CharacterActions'
import { CHARACTER_ACTION, CHARACTER_STATES, CHARACTER_TYPES } from '../../../f.assets/Characters/scripts/constants'
import Minion from '../classes/Minion'
import GameElement, { GameElementType } from '../GameElement/GameElement'
import getGameElementByUUID from '../helpers/getGameElementByUUID'

const GameMinion = (props: GameElementType) => {
  const { children, ...moreProps } = props
  const [uuid, setUUID] = useState<string>()
  const IndicatorRef = useRef<Group>(null)
  const battleState = useSceneStore(SEL_BATTLE_STATE)
  const battleIsActive = battleState === BATTLE_STATES.ONGOING

  return (
    <>
      <GameElement
        onUpdate={obj => IndicatorRef.current?.position.copy(obj.position)}
        elementClass={Minion}
        {...moreProps}
        addedToScene={(obj: Object3D) => {
          if (props?.addedToScene) {
            props?.addedToScene(obj)
          }
          setUUID(obj.uuid)
          // handleOnAddedToScene(state)
        }}
      >
        {children}
      </GameElement>
      <group ref={IndicatorRef}>{uuid && battleIsActive && <InfoComponent uuid={uuid} />}</group>
    </>
  )
}

export default GameMinion

const getIconFromAction = (action: CharacterAction | undefined) => {
  switch (action?.type) {
    case CHARACTER_ACTION.LOOT:
      return '🍬'
    case CHARACTER_ACTION.GOTO:
      return '👣'
    case CHARACTER_ACTION.ATTACKTO:
      return '⚔️'
    case CHARACTER_ACTION.AWAIT:
      return '❓'
    default:
      return ''
  }
}

const ActionIcon = ({ icon, team }: { icon: string; team: CHARACTER_TYPES }) => {
  const [currentIcon, setCurrentIcon] = useState<string>()

  const updateIcon = useCallback(
    (newIcon: string) => {
      setCurrentIcon('')
      requestAnimationFrame(() => setCurrentIcon(newIcon))
    },
    [setCurrentIcon],
  )

  useEffect(() => {
    if (icon) {
      updateIcon(icon)
    }
  }, [icon])
  const teamClass = team === CHARACTER_TYPES.JACK ? 'team-jack' : 'team-witch'

  return <span className={`stateIcon ${teamClass} ${currentIcon !== '' ? 'animate' : ''}`}>{currentIcon}</span>
}

const InfoComponent = ({ uuid }: { uuid: string }) => {
  const state = useSceneStore(SEL_STATE_BY_UUID(uuid)) as CharacterBaseState
  const team = useSceneStore(SEL_TEAM_BY_UUID(uuid)) as CHARACTER_TYPES.JACK | CHARACTER_TYPES.WITCH
  const selectedId = useSceneStore(SEL_SELECTED_STATE_UUID)
  const isSelected = selectedId === uuid
  const color =
    state?.owner === COMMANDERS.NOBODY ? '#999999' : team === CHARACTER_TYPES.JACK ? '#ff7c5b' : '#c460ff'
  const bgcolor =
    state?.owner === COMMANDERS.NOBODY ? '#333333' : team === CHARACTER_TYPES.JACK ? '#a12808' : '#3d1156'
  const gameObject = getGameElementByUUID(uuid) as Minion
  const actionIcon = getIconFromAction(gameObject.currAction)

  if (
    state?.state &&
    [CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(state?.state)
  ) {
    return <></>
  }
  return (
    <>
      <Html
        transform
        pointerEvents="none"
        distanceFactor={10}
        position={[0, 2.5, 0]}
        rotation={[0, -Math.PI / 2, 0]}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: 40,
          // transform: 'translate(-50%, 0)',
        }}
      >
        <ActionIcon icon={actionIcon} team={team} />
        <HPBar progress={Math.round((state?.hp! / state?.hp_max!) * 100)} color={color} bgcolor={bgcolor} />
      </Html>
      {isSelected && (
        <Html
          transform
          pointerEvents="none"
          distanceFactor={10}
          position={[0.5, 0, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: 40,
            transform: 'translate(-50%, 0)',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              background:
                'radial-gradient(circle at 50% center, rgba(51, 51, 51, 0) 43%, rgb(238, 238, 238) 64%, rgb(238, 238, 238) 64%, rgba(51, 51, 51,0) 67%)',
            }}
          />
        </Html>
      )}
    </>
  )
}

const HPBar = ({
  progress = 50,
  color = 'gray',
  bgcolor = 'white',
}: {
  progress?: number
  color?: string
  bgcolor?: string
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '0.2rem',
      width: '20px',
      border: '1px solid black',
      borderRadius: '2rem',
      backgroundColor: bgcolor,
    }}
  >
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '2rem',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.5s',
        willChange: 'transform',
        boxShadow: `0 0 5px ${color}`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          height: '100%',
          width: `${progress}%`,
          transition: 'width 1s ease-in-out',
          content: '',
          backgroundColor: color,
          top: 0,
          bottom: 0,
          left: 0,
          borderRadius: 'inherit',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      ></div>
    </div>
  </div>
)
